/* ==========================================================================
   ECOFUEGO — PDP video behaviour  ·  ecofuego-pdp-video.js
   --------------------------------------------------------------------------
   Loaded once per page from sections/main-product.liquid, on `pdp-` templates
   only. Two jobs, both opt-in per video from the LP media block:

     data-ef-video-auto      play when in view, pause when out of view
     data-ef-video-controls  no control bar until hover or keyboard focus

   WHY THIS ISN'T JUST THE `autoplay` ATTRIBUTE
   --------------------------------------------
   A product page here carries up to ten videos and the product films run
   9–46MB. `autoplay` alone tells the browser to start fetching all of them
   on load, and roughly three quarters of this store's traffic is on a phone.
   So the markup ships preload="none" and this file does two things the
   attribute cannot: it only attaches a source-load when the video is near
   the viewport, and it pauses anything that scrolls away so the decoder
   isn't running on six clips at once.

   WHY THE CONTROLS ARE TOGGLED IN JS
   ----------------------------------
   The native control bar lives in shadow DOM. ::-webkit-media-controls is
   Chromium-only, so hiding it in CSS would leave Firefox and Safari with a
   control bar sitting on an otherwise clean autoplaying video — the exact
   thing this is meant to remove. Adding and removing the attribute is the
   only approach that behaves the same in all three.

   ACCESSIBILITY
   -------------
   Controls appear on `focusin` as well as hover, so a keyboard user can
   still reach them; the video keeps tabindex through the controls attribute
   once it is on. And a visitor who has asked their OS for reduced motion
   gets no autoplay at all — an autoplaying loop is motion, whatever else it
   is. They keep a normal poster-and-controls video.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Controls on hover / focus ─────────────────────────────────────────── */
  function wireControls(video) {
    var show = function () {
      video.setAttribute('controls', '');
    };
    var hide = function () {
      /* Never take the controls away mid-interaction. Someone who has paused
         the video, or dragged the scrubber, is using them — pulling the bar
         out from under the cursor on mouseleave is how you lose a scrub. */
      if (video.paused) return;
      video.removeAttribute('controls');
    };

    video.addEventListener('mouseenter', show);
    video.addEventListener('focusin', show);
    video.addEventListener('mouseleave', hide);
    video.addEventListener('focusout', hide);

    /* Touch has no hover. One tap reveals the controls and pauses, which is
       what a tap on a playing video is asking for either way. */
    video.addEventListener(
      'touchstart',
      function () {
        show();
      },
      { passive: true }
    );
  }

  /* ── Play in view, pause out of view ───────────────────────────────────── */
  function wireAutoplay(videos) {
    if (!videos.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      /* Fail to a normal video: real preload, real controls, no motion. */
      videos.forEach(function (video) {
        video.removeAttribute('autoplay');
        video.setAttribute('controls', '');
        video.preload = 'metadata';
      });
      return;
    }

    /* Two observers with different margins, because "start downloading" and
       "start playing" are different moments. Loading 400px early means the
       first frame is there when the video arrives; playing 400px early means
       the visitor misses the opening of a clip they never saw. */
    var loader = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          /* ABOVE-THE-FOLD RACE — do not remove this guard.
             `load()` resets the media element, and resetting it ABORTS any
             play() already in flight. For a video below the fold that never
             collides: the 400px margin means this observer fires well before
             the player one does. For a video that is already on screen at
             load — the hero — both observers fire in the same tick, load()
             kills the play(), the rejection is swallowed by design, and
             nothing ever retries. The hero sat on its poster frame forever.
             Found 2026-09-12.

             So: anything that already has a source in hand is left alone.
             It does not need us; it was shipped with preload="auto" for
             exactly this reason. */
          if (entry.target.preload === 'auto' || entry.target.readyState > 0) {
            loader.unobserve(entry.target);
            return;
          }

          entry.target.preload = 'auto';
          entry.target.load();
          loader.unobserve(entry.target);
        });
      },
      { rootMargin: '400px 0px' }
    );

    var player = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            /* play() rejects when the browser blocks it — a muted inline
               video almost never is, but a rejected promise with no catch
               is an unhandled rejection in the console on the pages where
               it does. Swallow it: a video that didn't autoplay is not an
               error the visitor needs to know about. */
            var attempt = video.play();
            if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    videos.forEach(function (video) {
      loader.observe(video);
      player.observe(video);
    });
  }

  function init() {
    var scope = document.getElementById('MainContent') || document;

    Array.prototype.forEach.call(scope.querySelectorAll('[data-ef-video-controls]'), wireControls);

    wireAutoplay(Array.prototype.slice.call(scope.querySelectorAll('[data-ef-video-auto]')));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
